import { useLocation } from "react-router-dom"
import { useAppSelector } from "../../store/storeHooks"
import { selectUserId } from "../loginSelectors"
import animation from "../../../shared/animations/succeededAnimation.json"
import { confirmEmailAddress } from "../../profile/profileApi"
import { Typography, Box, Stack } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { LocalizedStrict } from "../../localization/components/AppLocalized"
import { useApiMutation } from "../../../core/api/useApiMutation"
import { Player } from "@lottiefiles/react-lottie-player"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleExclamation } from "@fortawesome/pro-regular-svg-icons"
import { QueryErrorBoundary } from "../../../core/api/components/QueryErrorBoundary"
import { Suspense, useEffect, useState } from "react"
import { LoadingSpinner } from "../../../shared/components/loading/LoadingSpinner"
import { enqueueSnackbar } from "notistack"
import { useAppLocalization } from "../../localization/components/AppLocalizationProvider"

const useStyles = makeStyles()((theme) => ({
    animationArea: {
        width: "7.125rem",
        height: "6.75rem",
        margin: "0 auto",
    },
    spinnerContainer: {
        minHeight: "20rem",
    },
    errorContainer: {
        minHeight: "20rem",
        padding: "2rem",
        maxWidth: "37.5rem",
        margin: "0 auto",
    },
    errorIcon: {
        fontSize: "3rem",
        color: theme.palette.tailwind.red[500],
    },
}))

const ConfirmAccountEmailComponent = () => {
    const location = useLocation()
    const { classes } = useStyles()
    const urlParams = new URLSearchParams(location.search)
    const confirmOtp = urlParams.get("confirmOtp")
    const userId = useAppSelector(selectUserId)
    const { l10n } = useAppLocalization()

    const {
        isLoading,
        isSuccess,
        isError: isApiError,
        mutateAsync: callChangeAccount,
    } = useApiMutation(confirmEmailAddress, {})
    const [hasError, setHasError] = useState<boolean>(false)

    useEffect(() => {
        if (userId && confirmOtp && !isSuccess && !isLoading && !hasError && !isApiError) {
            callChangeAccount({ userId, otp: confirmOtp }).catch(() => {
                setHasError(true)
            })
        } else if ((!userId || !confirmOtp) && !hasError && !isApiError) {
            setHasError(true)
            enqueueSnackbar({
                variant: "error",
                message: l10n.getString("confirm-email-missing-userid-or-otp"),
            })
        }
    }, [userId, confirmOtp, isSuccess, isLoading, isApiError, callChangeAccount, l10n, hasError])

    if (isSuccess) {
        return (
            <Stack gap="4rem" marginTop="6rem">
                <Box className={classes.animationArea}>
                    <Player src={animation} autoplay keepLastFrame />
                </Box>
                <LocalizedStrict id="confirm-email-success-message">
                    <Typography textAlign="center" variant="h5">
                        You can now switch between your user accounts
                    </Typography>
                </LocalizedStrict>
            </Stack>
        )
    }

    if (hasError || isApiError) {
        return (
            <Stack
                direction="column"
                justifyContent="center"
                alignItems="center"
                gap="1.5rem"
                className={classes.errorContainer}
            >
                <FontAwesomeIcon icon={faCircleExclamation} className={classes.errorIcon} />
                <LocalizedStrict id="confirm-email-error-message">
                    <Typography variant="h5" textAlign="center">
                        We could not confirm your email
                    </Typography>
                </LocalizedStrict>
            </Stack>
        )
    }

    return (
        <Stack justifyContent="center" alignItems="center" className={classes.spinnerContainer}>
            <LoadingSpinner />
        </Stack>
    )
}

export const ConfirmAccountEmail = () => (
    <QueryErrorBoundary>
        <Suspense fallback={null}>
            <ConfirmAccountEmailComponent />
        </Suspense>
    </QueryErrorBoundary>
)
